import classNames from 'classnames';
import { useRef, useState } from 'react';
import Image from 'next/legacy/image';
import { autoUpdate, flip, offset } from '@floating-ui/react-dom';
import { CardHeading } from '@styled-icons/bootstrap';
import {
  ArrowRight,
  MousePointer,
  Trash,
  Image as ImageIcon,
  Link as LinkIcon,
} from '@styled-icons/feather';
import { InterpreterFrom } from 'xstate';
import { useActor } from '@xstate/react';
import {
  colorsGradient,
  computeColor,
  fonts,
  NavbarDropdown,
  NavbarDropdownMenuItem,
  textColorsBasic,
} from '@typedream/core';
import { Description } from '@styled-icons/material';
import { Floating } from '../../../../../components/Floating';
import { useOutsideEventWithCanvas } from '../../../../../utils/eventAlerter';
import Menu from '../../../../Navbar/Menu';
import navbarSettingStyles from '../../../../Navbar/NavbarSettingToolbar/NavbarSettingToolbar.module.css';
import sharedStyles from '../../../../Navbar/Shared.module.css';
import { LeftMenuItem } from '../../../../Navbar/LeftMenu';
import { MenuSectionVertical } from '../../../../Navbar/Menu/MenuSectionVertical';
import { SettingButton } from '../../../../../nodes/navbar/components/DropdownMenu/SettingButton';
import { navbarDropdownContainerFSM } from '../finiteStateMachines/NavbarDropdownContainer.fsm';
import SelectColor from '../../../../Navbar/SelectColor';
import { SelectMedia } from '../../../../../components/SelectMedia';
import { LinkSuggestion } from '../../../../SideToolbar/items/Link';
import { getTPlugin, useTPlateEditorRef } from '../../../../../plate';
import { InternalLinkOptions } from '../../../../../plugins/internalLink/types';
import { KEY_INTERNAL_LINK } from '../../../../../pluginKeys';
import sidebarStyles from '../../../../SideToolbar/SideToolbar.module.css';
import dropdownMenuStyles from './DropdownMenu.module.css';

export const DEFAULT_FONT_FAMILY_NAME = 'inter';
export const FONT_FAMILIES_DEFAULT = {
  [DEFAULT_FONT_FAMILY_NAME]: fonts.inter,
};
const FONT_FAMILIES = { ...FONT_FAMILIES_DEFAULT, ...fonts };

export function FontIcon() {
  return (
    <div
      style={{
        textAlign: 'center',
        verticalAlign: 'center',
        lineHeight: '20px',
        height: '20px',
        width: '20px',
        cursor: 'pointer',
      }}
    >
      Tt
    </div>
  );
}

export function ColumnItemSettingToolbar(props: {
  machineActor: InterpreterFrom<typeof navbarDropdownContainerFSM>;
}) {
  const { machineActor } = props;
  const editor = useTPlateEditorRef();
  const { options } = getTPlugin<InternalLinkOptions>(editor, KEY_INTERNAL_LINK);

  const [machineState, machineSend] = useActor(machineActor);
  const outsideDetectRef = useOutsideEventWithCanvas<HTMLDivElement>('click', () => {
    machineSend({ type: 'ON_MOUSE_LEAVE_ITEM', payload: { state: false } });
  });

  const getCurrentLinkPageName = ({
    pageId,
    isInternalLink,
  }: {
    pageId?: string;
    isInternalLink: boolean;
  }) => {
    if (isInternalLink && pageId) {
      return options?.pagesPaths?.[pageId].name;
    }
    return undefined;
  };

  const getSelectedColumnItem = (context: typeof machineState.context) => {
    if (
      context?.dataNavbar === undefined ||
      context?.menuType === undefined ||
      context?.menuType === 'brand' ||
      context?.menuIndex === undefined
    )
      return undefined;

    if (context?.selectedColumnIdx !== undefined && context?.selectedColumnItemIdx !== undefined) {
      return (context.dataNavbar[context.menuType][context.menuIndex] as NavbarDropdown).menu
        .children[context.selectedColumnIdx].children[
        context.selectedColumnItemIdx
      ] as NavbarDropdownMenuItem;
    }

    if (context?.selectedColumnIdx === undefined && context?.selectedColumnItemIdx !== undefined) {
      return (context.dataNavbar[context.menuType][context.menuIndex] as NavbarDropdown).menu
        .children[context.selectedColumnItemIdx] as NavbarDropdownMenuItem;
    }

    return undefined;
  };

  const currentSelectedColumnItem = getSelectedColumnItem(machineState.context);

  const [open, setOpen] = useState(false);

  const [isTitleColorMenuOpen, setIsTitleColorMenuOpen] = useState(false);
  const [isDescriptionColorMenuOpen, setIsDescriptionColorMenuOpen] = useState(false);
  const [isLinkMenuOpen, setIsLinkMenuOpen] = useState(false);
  const [isFontFamilyMenuOpen, setIsFontFamilyMenuOpen] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);
  return (
    <div className={navbarSettingStyles.navbarMenuOptionsContainer} ref={outsideDetectRef}>
      <div
        style={{
          position: 'absolute',
          right: 0,
          bottom: -45,
        }}
        ref={ref}
      >
        <SettingButton onClick={() => setOpen(!open)}>Item Settings</SettingButton>
      </div>
      {open && (
        <Floating
          reference={ref}
          options={{
            placement: 'right-start',
            middleware: [offset(5), flip()],
            whileElementsMounted: autoUpdate,
          }}
          autoUpdate
        >
          <Menu>
            <div className={navbarSettingStyles.navbarMenuOptions} data-testid="NavbarMenuOptions">
              <div className={classNames(sharedStyles.optionTitle, navbarSettingStyles.title)}>
                Settings
              </div>

              {/* Title */}
              <LeftMenuItem
                icon={<CardHeading size={15} />}
                title={
                  <input
                    className={classNames(sharedStyles.input, navbarSettingStyles.menuInput)}
                    type="text"
                    value={currentSelectedColumnItem?.title ?? ''}
                    placeholder="Section Title"
                    onChange={(e) => {
                      machineSend({
                        type: 'ON_CHANGE_COLUMN_ITEM_TITLE',
                        payload: { newTitle: e.target.value },
                      });
                    }}
                  />
                }
                command={
                  // eslint-disable-next-line max-len
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div
                    style={{
                      background: computeColor(
                        currentSelectedColumnItem?.titleColor,
                        textColorsBasic,
                        '#000000'
                      ),
                    }}
                    className={sharedStyles.colorIcon}
                    onClick={() => setIsTitleColorMenuOpen(true)}
                  />
                }
                menuOpen={isTitleColorMenuOpen}
                onMenuClose={() => setIsTitleColorMenuOpen(false)}
              >
                <SelectColor
                  title="Text Colors"
                  onSelect={(value) => {
                    machineSend({
                      type: 'ON_CHANGE_COLUMN_ITEM_TITLE',
                      payload: { newColor: value },
                    });
                  }}
                  currentColor={currentSelectedColumnItem?.titleColor}
                  colors={[
                    // [DEFAULT_ACCENT_NAME, COLORS[DEFAULT_ACCENT_NAME]],
                    Object.entries(textColorsBasic),
                    Object.entries(colorsGradient),
                  ]}
                />
              </LeftMenuItem>
              {/* Title */}

              {/* Description */}
              <LeftMenuItem
                icon={<Description size={15} style={{ marginBottom: '2rem' }} />}
                title={
                  <>
                    <input
                      className={classNames(sharedStyles.input, navbarSettingStyles.menuInput)}
                      type="text"
                      value={currentSelectedColumnItem?.description ?? ''}
                      placeholder="Type your product description"
                      onChange={(e) => {
                        machineSend({
                          type: 'ON_CHANGE_COLUMN_ITEM_DESCRIPTION',
                          payload: { newDescription: e.target.value },
                        });
                      }}
                    />
                    <div
                      className="mt-2"
                      style={{
                        fontSize: 10,
                        lineHeight: 1,
                        color: 'var(--text-lighter)',
                        textTransform: 'none',
                      }}
                    >
                      <span className="is-block mb-1">Recommended: 50 characters</span>
                      <span className="is-block">
                        You&apos;ve used{' '}
                        <span
                          className={
                            (currentSelectedColumnItem?.description?.length ?? 0) > 50
                              ? 'with-danger-text-color'
                              : 'with-text-color'
                          }
                        >
                          {currentSelectedColumnItem?.description?.length}
                        </span>
                      </span>
                    </div>
                  </>
                }
                command={
                  // eslint-disable-next-line max-len
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div
                    style={{
                      background: computeColor(
                        currentSelectedColumnItem?.descriptionColor,
                        textColorsBasic,
                        '#000000'
                      ),
                      marginBottom: '2rem',
                    }}
                    className={sharedStyles.colorIcon}
                    onClick={() => setIsDescriptionColorMenuOpen(true)}
                  />
                }
                menuOpen={isDescriptionColorMenuOpen}
                onMenuClose={() => setIsDescriptionColorMenuOpen(false)}
              >
                <SelectColor
                  title="Text Colors"
                  onSelect={(value) => {
                    machineSend({
                      type: 'ON_CHANGE_COLUMN_ITEM_DESCRIPTION',
                      payload: { newColor: value },
                    });
                  }}
                  currentColor={currentSelectedColumnItem?.descriptionColor}
                  colors={[
                    // [DEFAULT_ACCENT_NAME, COLORS[DEFAULT_ACCENT_NAME]],
                    Object.entries(textColorsBasic),
                    Object.entries(colorsGradient),
                  ]}
                />
              </LeftMenuItem>
              {/* Description */}

              {/* Image */}
              <LeftMenuItem
                icon={<ImageIcon size={15} />}
                title="Select Icon"
                onClick={() => machineSend({ type: 'ON_CLICK_ICON_MENU' })}
                menuOpen={machineState.hasTag('ui:show_icon_menu')}
                onMenuClose={() => machineSend({ type: 'ON_CLOSE_ICON_MENU' })}
              >
                {machineState.hasTag('ui:show_icon_menu') && (
                  <SelectMedia
                    tabs={{
                      upload: {
                        accept: 'image/*',
                        button: 'Choose an image',
                        onSelect: async (file) => {
                          if (!editor.fileManager) return;
                          machineSend({ type: 'ON_UPLOAD_COLUMN_ITEM_ICON', newFile: file });
                        },
                      },
                      resources: {
                        onSelect: (url) => {
                          machineSend({
                            type: 'ON_CHANGE_COLUMN_ITEM_ICON',
                            payload: { icon: url },
                          });
                        },
                      },
                      link: {
                        button: 'Use this image',
                        placeholder: 'https://some-image-url.com/your-image',
                        defaultValue: currentSelectedColumnItem?.icon,
                        onSelect: (url) => {
                          machineSend({
                            type: 'ON_CHANGE_COLUMN_ITEM_ICON',
                            payload: { icon: url },
                          });
                        },
                      },
                    }}
                  />
                )}
              </LeftMenuItem>
              {/* Image */}

              <MenuSectionVertical>
                {/* Link */}
                <LeftMenuItem
                  icon={<LinkIcon size={15} />}
                  title={
                    <>
                      <input
                        className={classNames(
                          navbarSettingStyles.input,
                          navbarSettingStyles.menuInput
                        )}
                        type="text"
                        onFocus={() => setIsLinkMenuOpen(true)}
                        value={currentSelectedColumnItem?.url}
                        placeholder="URL"
                        onChange={(e) => {
                          machineSend({
                            type: 'ON_CHANGE_COLUMN_ITEM_LINK',
                            payload: { url: e.target.value },
                          });
                        }}
                        // data-testid="NavbarMenuOptions_ChangeUrl"
                      />
                      {currentSelectedColumnItem?.url && (
                        <div className="with-lighter-text-color is-size-7 ml-2">
                          {currentSelectedColumnItem?.internalLink
                            ? `Page: ${getCurrentLinkPageName({
                                pageId: currentSelectedColumnItem?.pageId,
                                isInternalLink: currentSelectedColumnItem?.internalLink ?? false,
                              })}`
                            : 'External Link'}
                        </div>
                      )}
                    </>
                  }
                  menuOpen={isLinkMenuOpen}
                >
                  <LinkSuggestion
                    onSave={(url, pageID, pagePath) => {
                      machineSend({
                        type: 'ON_CHANGE_COLUMN_ITEM_LINK',
                        payload: {
                          url,
                          pageID,
                          pagePath,
                        },
                      });
                    }}
                    inputURL={currentSelectedColumnItem?.url ?? ''}
                    internalPages={options?.pagesPaths}
                  />
                </LeftMenuItem>
                {/* Link */}
                {/* Link - Open in New Tab */}
                {
                  // Only show the open in new tab toggle if there's
                  // URL
                  currentSelectedColumnItem?.url && (
                    <div
                      className="pr-3 pb-0 is-flex is-align-items-center is-justify-content-space-between"
                      style={{ paddingLeft: '2.5em' }}
                    >
                      <div className={classNames(sidebarStyles.flex1)}>
                        <div className="with-lighter-text-color is-size-7">Open in new tab</div>
                      </div>

                      <div className={sidebarStyles.switchContainer}>
                        <input
                          id="toolbar-link-input"
                          type="checkbox"
                          name="publish"
                          className="switch is-rounded is-small is-primary"
                          checked={currentSelectedColumnItem.openInNewTab}
                          onClick={() => {}}
                          onChange={(e) => {
                            machineSend({
                              type: 'ON_CHANGE_COLUMN_ITEM_LINK_OPEN_NEW_TAB',
                              payload: {
                                openNewTab: e.target.checked,
                              },
                            });
                          }}
                        />
                        <label htmlFor="toolbar-link-input" style={{ verticalAlign: 'middle' }}>
                          {/* no text label */}
                        </label>
                      </div>
                    </div>
                  )
                }
                {/* Link - Open in New Tab */}
              </MenuSectionVertical>

              <MenuSectionVertical>
                {/* Type */}
                <LeftMenuItem
                  icon={<MousePointer size={15} />}
                  title="Type"
                  command={<ArrowRight size={15} />}
                  openMenuOnClick
                >
                  <LeftMenuItem
                    title={
                      <div
                        className={classNames(
                          dropdownMenuStyles.itemIllustration,
                          dropdownMenuStyles.itemTitleOnly
                        )}
                      >
                        <div>
                          <div className={dropdownMenuStyles.itemTitle}>Product</div>
                        </div>
                      </div>
                    }
                    onClick={() =>
                      machineSend({
                        type: 'ON_CHANGE_COLUMN_ITEM_TYPE',
                        payload: { type: 'title_only' },
                      })
                    }
                  />
                  <LeftMenuItem
                    title={
                      <div className={classNames(dropdownMenuStyles.itemIllustration)}>
                        <div>
                          <div className={dropdownMenuStyles.itemTitle}>Product</div>
                          <div className={dropdownMenuStyles.itemDescription}>
                            Short description
                          </div>
                        </div>
                      </div>
                    }
                    onClick={() =>
                      machineSend({
                        type: 'ON_CHANGE_COLUMN_ITEM_TYPE',
                        payload: { type: 'title_desc' },
                      })
                    }
                  />
                  <LeftMenuItem
                    title={
                      <div
                        className={classNames(
                          dropdownMenuStyles.itemIllustration,
                          dropdownMenuStyles.itemSimple
                        )}
                      >
                        <Image
                          src="/images/cloud.svg"
                          height={12}
                          width={16}
                          className={dropdownMenuStyles.itemIcon}
                        />
                        <div className={dropdownMenuStyles.itemTitle}>Product</div>
                      </div>
                    }
                    onClick={() =>
                      machineSend({
                        type: 'ON_CHANGE_COLUMN_ITEM_TYPE',
                        payload: { type: 'simple' },
                      })
                    }
                  />
                  <LeftMenuItem
                    title={
                      <div
                        className={classNames(
                          dropdownMenuStyles.itemIllustration,
                          dropdownMenuStyles.itemShort
                        )}
                      >
                        <Image
                          src="/images/cloud.svg"
                          height={16}
                          width={22}
                          className={dropdownMenuStyles.itemIcon}
                        />
                        <div>
                          <div className={dropdownMenuStyles.itemTitle}>Product</div>
                          <div className={dropdownMenuStyles.itemDescription}>
                            Short description
                          </div>
                        </div>
                      </div>
                    }
                    onClick={() =>
                      machineSend({
                        type: 'ON_CHANGE_COLUMN_ITEM_TYPE',
                        payload: { type: 'short' },
                      })
                    }
                  />
                  <LeftMenuItem
                    title={
                      <div
                        className={classNames(
                          dropdownMenuStyles.itemIllustration,
                          dropdownMenuStyles.itemLong
                        )}
                      >
                        <div>
                          <Image
                            src="/images/cloud.svg"
                            height={12}
                            width={16}
                            className={dropdownMenuStyles.itemIcon}
                          />
                          <div className={dropdownMenuStyles.itemTitle}>Product</div>
                        </div>
                        <div className={dropdownMenuStyles.itemDescription}>
                          Some longer description about this menu item
                        </div>
                      </div>
                    }
                    onClick={() =>
                      machineSend({ type: 'ON_CHANGE_COLUMN_ITEM_TYPE', payload: { type: 'long' } })
                    }
                  />
                  <LeftMenuItem
                    title={
                      <div
                        className={classNames(
                          dropdownMenuStyles.itemIllustration,
                          dropdownMenuStyles.itemFeatured
                        )}
                      >
                        <div className={dropdownMenuStyles.itemIcon} />
                        <div className={dropdownMenuStyles.itemTitle}>Featured Product</div>
                        <div className={dropdownMenuStyles.itemDescription}>
                          Accentuate a product on your menu using this style
                        </div>
                      </div>
                    }
                    onClick={() =>
                      machineSend({
                        type: 'ON_CHANGE_COLUMN_ITEM_TYPE',
                        payload: { type: 'featured' },
                      })
                    }
                  />
                </LeftMenuItem>
                {/* Type */}

                {/* Delete */}
                <LeftMenuItem
                  icon={<Trash size={15} />}
                  title="Delete Item"
                  onClick={() => machineSend({ type: 'ON_DELETE_COLUMN_ITEM' })}
                />
                {/* Delete */}
              </MenuSectionVertical>
              <MenuSectionVertical>
                {/* Font Family */}
                <LeftMenuItem
                  title="Font Family"
                  icon={<FontIcon />}
                  command={<ArrowRight size={15} />}
                  onClick={() => setIsFontFamilyMenuOpen(true)}
                  menuOpen={isFontFamilyMenuOpen}
                >
                  {Object.entries(fonts).map(([k, v]) => (
                    <LeftMenuItem
                      key={k}
                      title={
                        <span
                          style={{
                            // TODO(@ibedwi): fix type
                            // @ts-ignore
                            fontFamily: FONT_FAMILIES[k],
                            cursor: 'pointer',
                          }}
                        >
                          {v}
                        </span>
                      }
                      onClick={() => {
                        machineSend({ type: 'ON_CHANGE_COLUMN_ITEM_FONT', payload: { font: k } });
                      }}
                    />
                  ))}
                </LeftMenuItem>
                {/* Font Family */}
              </MenuSectionVertical>
            </div>
          </Menu>
        </Floating>
      )}
    </div>
  );
}
